import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { collectSimplePhotoUrls, readSimpleFlowState } from "@/lib/simpleTurnover";
import { issueTypeLabel } from "@/lib/issueTriage";
import type { JobEvidenceTask } from "@/components/owner/JobEvidenceView";

export interface PastCleaning {
  id: string;
  property_id: string;
  cleaning_task_id: string | null;
  submitted_at: string;
  duration_minutes: number | null;
  property_name: string;
  property_address: string | null;
  property_photo_url: string | null;
  property_unit: string | null;
  tasks_completed: number;
  total_tasks: number;
  completion_percentage: number | null;
  photo_count: number;
  keys_label: "Keys left" | "No dropoff";
  issue_missing: number;
  issue_broken: number;
  inventory_completed: boolean;
  inventory_low_count: number | null;
  cleaning_task: JobEvidenceTask | null;
  cleaner_name: string | null;
}

function propertyLabel(name: string, unit: string | null): string {
  const n = name.trim() || "Property";
  const u = unit?.trim();
  return u ? `${n} · ${u}` : n;
}

/**
 * Past jobs for the signed-in cleaner.
 * Source of truth = completed cleaning_tasks (simple turnover does not always write cleaning_submissions).
 */
export const useCleanerPastCleanings = () => {
  const queryClient = useQueryClient();

  useEffect(() => {
    const channel = supabase
      .channel("cleaner-past-cleanings-realtime")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "cleaning_tasks",
        },
        (payload) => {
          const row = (payload.new ?? payload.old) as { status?: string | null } | null;
          if (!row || row.status === "completed" || payload.eventType === "UPDATE") {
            void queryClient.invalidateQueries({ queryKey: ["cleaner-past-cleanings"] });
          }
        },
      )
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "cleaning_submissions",
        },
        () => {
          void queryClient.invalidateQueries({ queryKey: ["cleaner-past-cleanings"] });
        },
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [queryClient]);

  return useQuery({
    queryKey: ["cleaner-past-cleanings"],
    queryFn: async (): Promise<PastCleaning[]> => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return [];

      const { data: cleaner } = await supabase
        .from("cleaners")
        .select("id, name")
        .eq("user_id", user.id)
        .maybeSingle();

      let taskQuery = supabase
        .from("cleaning_tasks")
        .select(
          "id, property_id, cleaner_id, assignee_user_id, status, completed_at, scheduled_date, duration_minutes, checklist_completed, inventory_completed, rating, rating_note, rated_at, closeout_keys_confirmed, closeout_keys_photo_url, closeout_notes, entry_walkthrough_state",
        )
        .eq("status", "completed")
        .order("completed_at", { ascending: false, nullsFirst: false })
        .limit(50);

      if (cleaner?.id) {
        taskQuery = taskQuery.or(`cleaner_id.eq.${cleaner.id},assignee_user_id.eq.${user.id}`);
      } else {
        taskQuery = taskQuery.eq("assignee_user_id", user.id);
      }

      const { data: tasks, error } = await taskQuery;
      if (error) {
        console.error("[useCleanerPastCleanings] Error fetching completed tasks:", error);
        return [];
      }
      if (!tasks?.length) return [];

      const propertyIds = [...new Set(tasks.map((t) => t.property_id).filter(Boolean))];
      const { data: properties } = await supabase
        .from("properties")
        .select("id, name, address, property_photo_url, unit")
        .in("id", propertyIds);
      const propertyMap = new Map(
        (properties || []).map((p) => [
          p.id,
          {
            name: p.name,
            address: p.address,
            property_photo_url: p.property_photo_url,
            unit: (p as { unit?: string | null }).unit ?? null,
          },
        ]),
      );

      const taskIds = tasks.map((t) => t.id);
      const { data: maintRows } = await supabase
        .from("maintenance_issue_reports")
        .select("cleaning_task_id, response, photos")
        .in("cleaning_task_id", taskIds);

      const maintByTask = new Map<string, { response: string | null; photos: string[] | null }[]>();
      for (const row of maintRows || []) {
        const tid = row.cleaning_task_id;
        if (!tid) continue;
        const list = maintByTask.get(tid) ?? [];
        list.push(row);
        maintByTask.set(tid, list);
      }

      return tasks.map((taskRow) => {
        const task = taskRow as JobEvidenceTask & {
          property_id: string;
          completed_at?: string | null;
          scheduled_date?: string | null;
        };
        const property = propertyMap.get(task.property_id) ?? null;
        const simple = readSimpleFlowState(task.entry_walkthrough_state);
        const before = collectSimplePhotoUrls(simple, "before").length;
        const after = collectSimplePhotoUrls(simple, "after").length;
        const photo_count = before + after;

        const drop =
          simple.keys_drop_photo_url ||
          (simple.keys_photo_url &&
          task.closeout_keys_photo_url &&
          task.closeout_keys_photo_url !== simple.keys_photo_url
            ? task.closeout_keys_photo_url
            : !simple.keys_photo_url
              ? task.closeout_keys_photo_url
              : null);
        const keys_label: "Keys left" | "No dropoff" = drop ? "Keys left" : "No dropoff";

        let issue_missing = 0;
        let issue_broken = 0;
        const seen = new Set<string>();
        for (const r of maintByTask.get(task.id) ?? []) {
          if (issueTypeLabel(r.response) === "Broken") issue_broken += 1;
          else issue_missing += 1;
          for (const p of r.photos ?? []) if (p) seen.add(p);
        }
        for (const f of simple.inspect_findings ?? []) {
          if (f.status !== "missing" && f.status !== "broken") continue;
          if (f.evidence_photo_url && seen.has(f.evidence_photo_url)) continue;
          if (f.status === "broken") issue_broken += 1;
          else issue_missing += 1;
        }

        const submitted_at =
          task.completed_at ||
          task.scheduled_date ||
          new Date().toISOString();

        return {
          id: task.id,
          property_id: task.property_id,
          cleaning_task_id: task.id,
          submitted_at,
          duration_minutes: task.duration_minutes ?? null,
          property_name: propertyLabel(property?.name || "Property", property?.unit ?? null),
          property_address: property?.address || null,
          property_photo_url: property?.property_photo_url || null,
          property_unit: property?.unit ?? null,
          tasks_completed: 0,
          total_tasks: 0,
          completion_percentage: null,
          photo_count,
          keys_label,
          issue_missing,
          issue_broken,
          inventory_completed: !!task.inventory_completed,
          inventory_low_count: null,
          cleaning_task: task,
          cleaner_name: cleaner?.name ?? null,
        };
      });
    },
    retry: false,
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
  });
};
